import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { PAYMENT_METHODS, BANK_TRANSFER, ORDERS_WHATSAPP } from '../config/payment';

// Fila de dato copiable de la tarjeta de transferencia (CLABE, tarjeta, monto, etc.)
const CopyRow = ({ label, display, copyValue, field, copiedField, onCopy, copiedLabel = 'Copiado' }) => (
  <div className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 border border-blue-100">
    <div className="min-w-0">
      <p className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wide">{label}</p>
      <p className="text-gray-900 font-mono font-bold text-xs sm:text-sm break-all">{display}</p>
    </div>
    <button
      onClick={() => onCopy(copyValue, field)}
      className="flex-shrink-0 flex items-center gap-1 bg-blue-700 hover:bg-blue-800 text-white text-[10px] sm:text-xs font-bold px-2.5 sm:px-3 py-2 rounded-lg transition-colors duration-200 cursor-pointer min-h-[40px]"
      aria-label={`Copiar ${label}`}
    >
      {copiedField === field ? (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {copiedLabel}
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copiar
        </>
      )}
    </button>
  </div>
);

const ModalSuccess = ({ isOpen, onClose, orderData, orderCode }) => {
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  if (!isOpen || !orderData) return null;

  const isTransfer = orderData.paymentMethod === PAYMENT_METHODS.TRANSFER;

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  };

  const formatDisplayTime = (hour, minute) => {
    const numHour = parseInt(hour, 10);
    const numMinute = parseInt(minute, 10);
    const ampm = numHour >= 12 ? 'p.m.' : 'a.m.';
    const displayHour = numHour > 12 ? numHour - 12 : numHour === 0 ? 12 : numHour;
    return `${displayHour}:${numMinute.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const getDeliveryInfo = () => {
    if (orderData.deliveryType === 'pickup') {
      return {
        type: 'Recoger en tienda',
        address: 'Ver ubicación en Maps',
        isLink: true,
        url: 'https://maps.app.goo.gl/kbU64E5Kucu6Mpex5'
      };
    } else if (orderData.deliveryType === 'delivery') {
      return {
        type: 'A domicilio',
        address: 'Tu ubicación actual',
        isLink: false
      };
    } else if (orderData.deliveryType === 'someone_else') {
      const fullAddress = `${orderData.customAddress.street.trim()} ${orderData.customAddress.number.trim()}, ${orderData.customAddress.neighborhood.trim()}, Montemorelos, NL`;
      return {
        type: 'A otra ubicación',
        address: fullAddress,
        isLink: false
      };
    }
  };

  const deliveryInfo = getDeliveryInfo();
  const currentDate = new Date();
  const formattedCurrentDate = `${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getFullYear()}`;
  const formattedCurrentTime = `${currentDate.getHours().toString().padStart(2, '0')}:${currentDate.getMinutes().toString().padStart(2, '0')} ${currentDate.getHours() >= 12 ? 'p.m.' : 'a.m.'}`;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Animated Green Background */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-green-600 via-green-700 to-green-800"
        initial={{ clipPath: "circle(0% at 50% 50%)" }}
        animate={{ clipPath: "circle(150% at 50% 50%)" }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
      />

      {/* Content Container */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-3 sm:p-4 md:p-6 lg:p-8">
        <motion.div
          className={`relative w-full max-w-[95%] sm:max-w-lg md:max-w-xl ${isTransfer ? 'lg:max-w-4xl xl:max-w-5xl' : 'lg:max-w-2xl xl:max-w-3xl'} my-4 sm:my-6 lg:my-8`}
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
        >
          {/* Close button - Top right */}
          <button
            onClick={onClose}
            className="absolute -top-10 sm:-top-12 right-0 text-white/90 hover:text-white transition-all duration-200 z-20 p-2 hover:bg-white/10 rounded-full"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* White Ticket Card */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] sm:max-h-[90vh]">

            {/* Header - Green bar with LA JUANITA and Comprobante */}
            <div className="bg-green-700 px-4 sm:px-5 md:px-6 py-3 sm:py-4 flex justify-between items-center flex-shrink-0">
              <h1 className="text-white font-bold text-lg sm:text-xl md:text-2xl tracking-wide">LA JUANITA</h1>
              <div className="text-right">
                <p className="text-white font-semibold text-xs sm:text-sm md:text-base">Comprobante</p>
                <p className="text-white/90 text-[10px] sm:text-xs md:text-sm">{formattedCurrentDate} · {formattedCurrentTime}</p>
              </div>
            </div>

            {/* Main Content - Scrollable */}
            <div className={`px-4 sm:px-5 md:px-6 lg:px-8 xl:px-10 ${isTransfer ? 'py-4 sm:py-5 md:py-6' : 'py-6 sm:py-7 md:py-8 lg:py-10'} overflow-y-auto flex-1 custom-scrollbar`}>

              {/* Success Message with Checkmark */}
              <motion.div
                className={`flex items-start ${isTransfer ? 'mb-4 sm:mb-5' : 'mb-6 sm:mb-7 md:mb-8'}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
              >
                {/* Checkmark Circle */}
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-green-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                {/* Message */}
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1">Pedido recibido</h2>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base">
                    Guarda tu número de orden. Lo usaremos para entregarte.
                  </p>
                </div>
              </motion.div>

              {/* Order Number + datos de transferencia */}
              <div className={isTransfer ? 'mb-4 sm:mb-5 grid md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-4 sm:gap-5 items-start' : ''}>
                <div className="min-w-0">
                <motion.div
                  className="mb-5 sm:mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                >
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base font-medium mb-1 sm:mb-2 uppercase tracking-wide">Número de orden</p>
                  <div className={`font-black text-gray-900 leading-none ${isTransfer ? 'text-5xl sm:text-6xl md:text-7xl' : 'text-5xl sm:text-6xl md:text-7xl lg:text-8xl'}`}>
                    {orderCode}
                  </div>
                </motion.div>
              {/* Delivery Details */}
              <motion.div
                className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 mb-5 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.4 }}
              >
                {/* Entrega Row */}
                <div className="flex justify-between items-start mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200">
                  <span className="text-gray-600 text-xs sm:text-sm md:text-base font-medium">Entrega</span>
                  <div className="text-right">
                    <span className="inline-block bg-gray-900 text-white text-[10px] sm:text-xs md:text-sm font-bold px-2 sm:px-3 py-1 rounded-full">
                      {deliveryInfo.type}
                    </span>
                  </div>
                </div>

                {/* Cuándo Row */}
                <div className="flex justify-between items-center mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200">
                  <span className="text-gray-600 text-xs sm:text-sm md:text-base font-medium">Cuándo</span>
                  <span className="text-gray-900 text-xs sm:text-sm md:text-base font-bold text-right">
                    {formatDate(orderData.customerInfo.date)} · {formatDisplayTime(orderData.customerInfo.hour, orderData.customerInfo.minute)}
                  </span>
                </div>

                {/* Dónde Row */}
                <div className="flex justify-between items-start gap-3">
                  <span className="text-gray-600 text-xs sm:text-sm md:text-base font-medium flex-shrink-0">Dónde</span>
                  {deliveryInfo.isLink ? (
                    <a
                      href={deliveryInfo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-700 hover:text-green-800 text-xs sm:text-sm md:text-base font-bold text-right underline decoration-2 hover:decoration-green-800 transition-colors duration-200 flex items-center gap-1"
                    >
                      {deliveryInfo.address}
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : (
                    <span className="text-gray-900 text-xs sm:text-sm md:text-base font-bold text-right">
                      {deliveryInfo.address}
                    </span>
                  )}
                </div>

                {/* Pago Row */}
                <div className="flex justify-between items-center mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                  <span className="text-gray-600 text-xs sm:text-sm md:text-base font-medium">Pago</span>
                  <span className={`inline-block text-[10px] sm:text-xs md:text-sm font-bold px-2 sm:px-3 py-1 rounded-full ${isTransfer ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                    {isTransfer ? 'Transferencia' : 'Efectivo al entregar'}
                  </span>
                </div>
              </motion.div>

              {/* Total a Pagar */}
              <motion.div
                className="mb-5 sm:mb-6 pb-5 sm:pb-6 border-b border-gray-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.4 }}
              >
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm sm:text-base md:text-lg font-medium uppercase tracking-wide">Total a pagar</span>
                  <span className="text-gray-900 text-xl sm:text-2xl md:text-3xl font-black">
                    ${orderData.totalPrice} MXN
                  </span>
                </div>
                {orderData.customerDiscount > 0 && (
                  <div className="flex justify-between items-center mt-1.5">
                    <span className="text-green-600 text-xs sm:text-sm font-medium">
                      {orderData.customerDiscountPct === 100 ? 'Cortesía' : `Descuento cliente (${orderData.customerDiscountPct}%)`}
                    </span>
                    <span className="text-green-600 text-xs sm:text-sm font-bold">-${orderData.customerDiscount} MXN</span>
                  </div>
                )}
                {orderData.employeeDiscount > 0 && (
                  <div className="flex justify-between items-center mt-1.5">
                    <span className="text-blue-600 text-xs sm:text-sm font-medium">Beneficio de empleado</span>
                    <span className="text-blue-600 text-xs sm:text-sm font-bold">-${orderData.employeeDiscount} MXN</span>
                  </div>
                )}
                {orderData.couponDiscount > 0 && (
                  <div className="flex justify-between items-center mt-1.5">
                    <span className="text-purple-600 text-xs sm:text-sm font-medium">Cupón aplicado</span>
                    <span className="text-purple-600 text-xs sm:text-sm font-bold">-${orderData.couponDiscount} MXN</span>
                  </div>
                )}
                <p className="text-gray-500 text-xs sm:text-sm text-right mt-1">
                  {orderData.totalPrice === 0 ? 'Cortesía aplicada' : (isTransfer ? 'Por transferencia bancaria' : 'En efectivo, al recibir tu pedido')}
                </p>
              </motion.div>


                </div>
                {/* Datos de transferencia */}
                {isTransfer && (
                  <motion.div
                    className="bg-blue-50 border border-blue-200 rounded-xl sm:rounded-2xl p-3.5 sm:p-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0, duration: 0.4 }}
                  >
                  <h3 className="text-gray-900 font-bold text-sm sm:text-base mb-0.5 flex items-center gap-2">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
                    </svg>
                    Datos para tu transferencia
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-3">
                    {BANK_TRANSFER.bank} · {BANK_TRANSFER.beneficiary}
                  </p>

                  <div className="space-y-2">
                    <CopyRow
                      label="CLABE"
                      display={BANK_TRANSFER.clabe}
                      copyValue={BANK_TRANSFER.clabe}
                      field="clabe"
                      copiedField={copiedField}
                      onCopy={copyToClipboard}
                      copiedLabel="Copiada"
                    />
                    <CopyRow
                      label="Tarjeta"
                      display={BANK_TRANSFER.card.replace(/(\d{4})(?=\d)/g, '$1 ')}
                      copyValue={BANK_TRANSFER.card}
                      field="tarjeta"
                      copiedField={copiedField}
                      onCopy={copyToClipboard}
                      copiedLabel="Copiada"
                    />
                    <CopyRow
                      label="Cuenta"
                      display={BANK_TRANSFER.account}
                      copyValue={BANK_TRANSFER.account}
                      field="cuenta"
                      copiedField={copiedField}
                      onCopy={copyToClipboard}
                      copiedLabel="Copiada"
                    />
                    <CopyRow
                      label="Monto exacto"
                      display={`$${orderData.totalPrice} MXN`}
                      copyValue={orderData.totalPrice}
                      field="monto"
                      copiedField={copiedField}
                      onCopy={copyToClipboard}
                    />
                    <CopyRow
                      label="Concepto / Referencia"
                      display={orderCode}
                      copyValue={orderCode}
                      field="concepto"
                      copiedField={copiedField}
                      onCopy={copyToClipboard}
                    />
                  </div>

                  <p className="text-blue-900 text-xs sm:text-sm mt-3 leading-snug">
                    <span className="font-bold">Importante:</span> pon <span className="font-bold">{orderCode}</span> como concepto o referencia de tu transferencia (solo el número). Así identificamos tu pago automáticamente.
                  </p>

                  {ORDERS_WHATSAPP && (
                    <a
                      href={`https://wa.me/${ORDERS_WHATSAPP}?text=${encodeURIComponent(`Hola, soy ${orderData.customerInfo?.name || ''}. Pedido ${orderCode} — te envío mi comprobante de transferencia por $${orderData.totalPrice} MXN.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold text-xs sm:text-sm px-4 py-3 rounded-xl transition-colors duration-200 min-h-[44px]"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Enviar comprobante por WhatsApp
                    </a>
                  )}
                </motion.div>
                )}
              </div>

              {/* Ver detalles del pedido - Collapsible */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.4 }}
              >
                <button
                  onClick={() => setShowOrderDetails(!showOrderDetails)}
                  className="w-full flex justify-between items-center py-3 sm:py-4 px-4 sm:px-5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors duration-200 group"
                >
                  <span className="text-gray-700 font-semibold text-xs sm:text-sm md:text-base">Ver detalles del pedido</span>
                  <motion.svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 group-hover:text-gray-900 transition-colors flex-shrink-0"
                    animate={{ rotate: showOrderDetails ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </button>

                {/* Collapsible Order Details */}
                <AnimatePresence>
                  {showOrderDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-3 sm:pt-4 px-4 sm:px-5 space-y-3">
                        {/* List of Items */}
                        <div className="space-y-2">
                          {(orderData.items || []).filter(i => !i.comboGroupId).map((item, index) => (
                            <div key={index} className="flex justify-between items-start text-xs sm:text-sm">
                              <div className="flex items-start gap-2 pr-2 flex-1">
                                <span className="font-semibold text-gray-900 whitespace-nowrap">{item.quantity}x</span>
                                <div className="flex-1">
                                  <span className="text-gray-600 leading-snug">{item.title}</span>
                                  {item.presentationLabel && (
                                    <p className="text-gray-500 text-xs">
                                      {item.presentationLabel}{item.qty && item.unit ? ` · ${item.qty} ${item.unit}` : ''}
                                    </p>
                                  )}
                                  {item.selectedVariants && Object.keys(item.selectedVariants).length > 0 && (
                                    <div className="mt-0.5 space-y-0.5">
                                      {Object.entries(item.selectedVariants).map(([variantType, variantOption]) => {
                                        const variantPrice = item.variantsConfig?.[variantType]?.[variantOption];
                                        return (
                                          <p key={variantType} className="text-gray-500 text-xs">
                                            {variantType}: {variantOption} {variantPrice && `(+$${variantPrice})`}
                                          </p>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <span className="text-gray-900 font-medium whitespace-nowrap">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Combos */}
                        {orderData.comboGroups && orderData.comboGroups.length > 0 && (
                          <div className="space-y-2 pt-1">
                            {orderData.comboGroups.map((group, gi) => (
                              <div key={gi} className="text-xs sm:text-sm">
                                <div className="flex justify-between font-semibold text-gray-900">
                                  <span>{group.meta?.name}</span>
                                  <span>${group.meta?.price}</span>
                                </div>
                                {group.items.map((it, ii) => (
                                  <p key={ii} className="text-gray-500 pl-3">• {it.title} (porción)</p>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Financial Summary */}
                        <div className="pt-3 border-t border-gray-200 space-y-1">
                          {orderData.hasPromotions && (
                            <>
                              <div className="flex justify-between text-xs sm:text-sm text-gray-500">
                                <span>Subtotal</span>
                                <span>${orderData.originalTotal}</span>
                              </div>
                            </>
                          )}
                          {orderData.customerDiscount > 0 && (
                            <div className="flex justify-between text-xs sm:text-sm text-green-600 font-medium">
                              <span>{orderData.customerDiscountPct === 100 ? 'Cortesía' : `Descuento cliente (${orderData.customerDiscountPct}%)`}</span>
                              <span>-${orderData.customerDiscount}</span>
                            </div>
                          )}
                          {orderData.employeeDiscount > 0 && (
                            <div className="flex justify-between text-xs sm:text-sm text-blue-600 font-medium">
                              <span>Beneficio de empleado</span>
                              <span>-${orderData.employeeDiscount}</span>
                            </div>
                          )}
                          {orderData.couponDiscount > 0 && (
                            <div className="flex justify-between text-xs sm:text-sm text-purple-600 font-medium">
                              <span>Cupón aplicado</span>
                              <span>-${orderData.couponDiscount}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-gray-900 text-sm sm:text-base pt-1">
                            <span>Total</span>
                            <span>${orderData.totalPrice}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Footer Message */}
              <motion.div
                className={`${isTransfer ? 'mt-4 pt-4' : 'mt-6 sm:mt-7 md:mt-8 pt-5 sm:pt-6'} border-t border-gray-200`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.4 }}
              >
                <p className="text-center text-gray-700 text-xs sm:text-sm md:text-base leading-relaxed">
                  Recibe tu pedido con tu <span className="font-bold underline decoration-green-600 decoration-2">número de orden</span> o tu <span className="font-bold underline decoration-green-600 decoration-2">celular</span>.
                </p>
              </motion.div>

            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ModalSuccess;
